import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Redirect } from 'react-router-dom'
import CreatorFrame from '../../../../shared/CreatorFrame'
import { CAMPAIGNS_VISIBLE } from '../../../../../config/featureFlags'
import { useMarketingControl } from '../../../../marketing/SalonRunsMarketing'
import useSites, { groupSites } from '../../../../multiLocation/useSites'
import { usePlans } from '../../plans/usePlans'
import { blankCampaign } from '../data/build'
import { listCampaigns, hydrateCampaigns } from '../data/campaignStore'
import { readSeed, asDraft, campaignPath, planPath, LIST_PATH } from '../data/open'
import { useMarketingKit } from '../useMarketingKit'
import Studio from './Studio'
import StartPage from './StartPage'
import './Studio.css'

// Marketing › Campaigns › the Campaign Studio, at its own addresses:
//   /marketing-campaigns/new            where to start
//   /marketing-campaigns/new/:source    a new campaign (a plan's key, or "blank")
//   /marketing-campaigns/:id            a saved campaign
// One route renders all three, so the studio stays put when a new campaign's
// first save moves it to its own address.
const CampaignStudioPage = ({ match, location, history, dispatch }) => {
  const { source, id } = match.params
  const control = useMarketingControl()
  const k = useMarketingKit()
  const enabled = CAMPAIGNS_VISIBLE && !control.managed
  const { plans, templates, others, segments, things, loading } = usePlans({ dispatch, user: k.user, trade: k.trade, salonName: k.salonName, brandColour: k.brandColour, family: k.family, country: k.country, links: k.links, currency: k.currency, enabled })
  const { sites, active, inGroup, isOwnerHere, group: g } = useSites()
  // The locations in this group: a stylist at two separate salons has two
  // sites but no group, and nothing is copied between those
  const group = useMemo(() => groupSites({ sites, group: g }), [sites, g])
  const canCopy = inGroup && isOwnerHere && group.length > 1
  const from = (location.state && location.state.from) || null
  const [session, setSession] = useState(null) // { key, id?, source?, draft?, missing? }

  // What's open: a saved campaign, or a new one from where it started
  useEffect(() => {
    if (id) {
      if (session && session.id === id) return // this studio's own first save
      const rec = listCampaigns().find(c => c.id === id)
      if (rec) { setSession({ key: `id:${id}`, id, draft: asDraft(rec) }); return }
      // Not in this browser yet (made on another device, or by someone
      // else at the salon): ask the server before saying it isn't there.
      let alive = true
      setSession(null)
      hydrateCampaigns().then(() => {
        if (!alive) return
        const found = listCampaigns().find(c => c.id === id)
        setSession(found ? { key: `id:${id}`, id, draft: asDraft(found) } : { key: `missing:${id}`, missing: true })
      })
      return () => { alive = false } // eslint-disable-line consistent-return
    }
    if (source) {
      const wanted = decodeURIComponent(source)
      if (session && session.source === wanted && !session.missing) return
      const found = readSeed(wanted) || (wanted === 'blank' ? blankCampaign() : [...plans, ...templates].find(p => p.key === wanted))
      if (found) setSession({ key: `new:${wanted}:${Date.now()}`, source: wanted, draft: found })
      else setSession(loading ? null : { key: `missing:${wanted}`, source: wanted, missing: true })
      return
    }
    setSession(null)
  }, [id, source, loading]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!enabled) return <Redirect to={LIST_PATH} />

  // Back where they came from (Home, the calendar), else the campaigns list
  // Back to the page that opened the studio (it's the entry before this
  // one), so Back afterwards doesn't land on a second copy of it
  const leave = () => (from && history.length > 1 ? history.goBack() : history.replace(from || LIST_PATH, from ? {} : { showCampaigns: Date.now() }))

  if (!id && !source) {
    return <StartPage plans={plans} templates={templates} loading={loading} k={k} onOpen={plan => history.replace(planPath(plan), { from })} onNavigate={(path, state) => history.push(path, state)} onClose={leave} />
  }
  if (!session || session.missing) {
    return (
      <CreatorFrame name="Campaign" onClose={leave}>
        <div className="cst-missing">
          {session && session.missing ? (
            <>
              <h2>This campaign isn’t here</h2>
              <p>It may have been deleted, or it belongs to another location.</p>
              <button type="button" className="cst-primary" onClick={leave}>Back to campaigns</button>
            </>
          ) : <span className="mkp-spin" aria-label="Loading" />}
        </div>
      </CreatorFrame>
    )
  }
  return (
    <Studio
      key={session.key}
      initial={session.draft}
      k={k}
      data={{ others, segments, things, posts: (things && things.posts) || [] }}
      dispatch={dispatch}
      managed={control.managed}
      history={history}
      from={from}
      canCopy={canCopy}
      group={group}
      active={active}
      onExit={leave}
      onSavedId={(newId) => { setSession(s => ({ ...s, id: newId })); history.replace(campaignPath(newId), location.state) }}
    />
  )
}

export default connect(state => ({ user: state.user }))(CampaignStudioPage)
